import React, { useEffect, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import useExportToPDF from '../hooks/useExportToPDF';
import { SelectedFilter } from './Filters/types';
import { FilterTableKey } from '../redux/filtersSlice';
import ModalGlobal from './AppModal/ModalGlobal';
import { useGroupButtonsModal } from '../hooks/useGroupButtonsModal';
import { shareIndicator } from '../features/Dashboard/api/shareIndicator';
import { useTranslation } from 'react-i18next';
import { exportHistoryCsv } from '../features/historicalData/api/exportHistoryCsv';
import { ApiErrorResponse } from '../types/ApiErrorResponse';
import { useUserPermissions } from '../utils/auth';
import { PERMISSIONS } from '../constants/permissions';
import { addAuditLog } from '../features/auditLogs/api/auditApi';
import { AuditActionEnum, AuditLogRequestDto } from '../features/auditLogs/types';

type ChartShareActionsProps = {
  chartRef: React.RefObject<HTMLDivElement>;
  title: string;
  filters: SelectedFilter[];
  t: any;
  filtersTable?: FilterTableKey;
  exportCsvAction?: () => Promise<void>;
  exportCsvAudit?: AuditLogRequestDto;
};

const ChartShareActions = ({ chartRef, title, filters, t, filtersTable = 'dashboard', exportCsvAction, exportCsvAudit }: ChartShareActionsProps) => {
    const { t: tCommon } = useTranslation('');
    const permissions = useUserPermissions();

    const { showModal, showShareModal, showExportModal, modalData, error, endDate, closeModal, setError, setLoading, isLoading, openInfoModal, openShareModal, openExportModal } = useGroupButtonsModal();
    const defaultBody = tCommon("common:shareDefaultBody", { indicatorName: title || "" });
    const defaultSubject = tCommon("common:shareDefaultSubject", { indicatorName: title || "" });
    const [shareForm, setShareForm] = useState({
        to: "",
        subject: "",
        note: "",
    });

    const { exportToPDFComponentFeeling, exportToPDFComponentFeelingBlob } = useExportToPDF(t, filtersTable);

    const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
        const fetchError = error as ApiErrorResponse;
        return (
            fetchError?.message ||
            fallbackMessage
        );
    };

    useEffect(() => {
        if (!showShareModal) return;
        setShareForm(prev => ({
            ...prev,
            note: prev.note || defaultBody,
            subject: prev.subject || defaultSubject,
        }));
    }, [showShareModal, defaultBody, defaultSubject]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setShareForm(prev => ({ ...prev, [name]: value }));
    };

    const exportPDF = async () => {
        if (!chartRef.current) {
            throw new Error('Chart not ready for export.');
        }
        await exportToPDFComponentFeeling(chartRef.current, filters, title);
    };

    const sharePdf = async () => {
        if (!chartRef.current) {
            throw new Error('Chart not ready for export.');
        }
        return exportToPDFComponentFeelingBlob(chartRef.current, filters, title);
    };

    const exportCsv = async () => {
        if (!chartRef.current) {
            throw new Error('Chart not ready for export.');
        }
        if (exportCsvAction) {
            return exportCsvAction();
        }
        return exportHistoryCsv(filters);
    };

    const exportIndicatorAsPdf = async () => {
        setLoading(true);
        setError('');

        try {
            await exportPDF();
            await addAuditLog({
                entity: 'DashboardIndicator',
                action: AuditActionEnum.Print,
                source: window.location.pathname,
                description: `Indicator report printed: ${title}`,
                filters
            });
            closeModal();
        } catch (error) {
            const errorMessage = getErrorMessage(error, tCommon("common:requestError.errorBadRequest"));
            const fetchError = error as ApiErrorResponse;

            console.error('[ChartShareActions] Failed to export PDF', {
                indicatorTitle: title,
                filtersTable,
                status: fetchError?.status,
                message: errorMessage,
                error
            });
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const shareIndicatorPdfByEmail = async () => {
        setLoading(true);
        setError('');

        try {
            const pdfBlob = await sharePdf();
            await addAuditLog({
                entity: 'DashboardIndicator',
                action: AuditActionEnum.SendEmail,
                source: window.location.pathname,
                description: `Indicator report shared by email: ${title}`,
                filters
            });
            await shareIndicator({
                to: shareForm.to,
                subject: shareForm.subject,
                body: shareForm.note,
                file: pdfBlob,
                filename: "dashboard.pdf",
            });
            closeModal();
        } catch (error) {
            const errorMessage = getErrorMessage(error, tCommon("common:requestError.errorBadRequest"));
            const fetchError = error as ApiErrorResponse;
            console.error('[ChartShareActions] Failed to share indicator PDF', {
                indicatorTitle: title,
                filtersTable,
                recipient: shareForm.to,
                status: fetchError?.status,
                message: errorMessage,
                error
            });
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const exportIndicatorDataAsCsv = async () => {
        setLoading(true);
        setError('');

        try {
            await exportCsv();
            if (exportCsvAction) {
                if (exportCsvAudit?.entity) {
                    await addAuditLog(exportCsvAudit);
                }
            } else {
                await addAuditLog({
                    entity: 'DashboardIndicator',
                    action: AuditActionEnum.ExportCsv,
                    source: window.location.pathname,
                    description: `Indicator data exported to CSV: ${title}`,
                    filters
                });
            }
            closeModal();
        } catch (error) {
            const errorMessage = getErrorMessage(error, tCommon("common:requestError.errorBadRequest"));
            const fetchError = error as ApiErrorResponse;

            console.error('[ChartShareActions] Failed to export indicator CSV', {
                indicatorTitle: title,
                filtersTable,
                status: fetchError?.status,
                message: errorMessage,
                error
            });
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const shareIndicatorForm = () => {
        return (
            <Form>
                <Form.Group className="mb-3">
                    <Form.Label>Per</Form.Label>
                    <Form.Control type="text" name="to" className="form-control" value={shareForm.to} onChange={onChange} />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Assumpte</Form.Label>
                    <Form.Control type="text" name="subject" className="form-control" value={shareForm.subject} onChange={onChange} />
                </Form.Group>
                <Form.Group>
                    <Form.Label>Nota</Form.Label>
                    <Form.Control as="textarea" rows={3} name="note" className="form-control" value={shareForm.note} onChange={onChange} />
                </Form.Group>
            </Form>
        );
    };

    return (
        <div className="d-flex align-items-center">
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                subtitle2={modalData.subtitle2}
                saveText={tCommon("common:modalActionOK")}
                closeText={tCommon("common:modalActionNOTOK")}
                error={error}
                endDate={endDate}
                onSave={() => exportIndicatorAsPdf()}
                isLoading={isLoading}
            />
            <ModalGlobal
                show={showShareModal}
                onClose={closeModal}
                title={"compartir els resultats de: "}
                body={shareIndicatorForm()}
                subtitle={modalData.subtitle}
                subtitle2={modalData.subtitle2}
                saveText={tCommon("common:buttons.buttonSend")}
                closeText={tCommon("common:buttons.buttonCancel")}
                error={error}
                endDate={endDate}
                onSave={() => shareIndicatorPdfByEmail()}
                isLoading={isLoading}
            />
            <ModalGlobal
                show={showExportModal}
                onClose={closeModal}
                title={tCommon("dashboard:share.exportTitle")}
                contentType={modalData.type}
                subtitle={tCommon("dashboard:share.exportSubtitle",{indicatorName: title})}
                subtitle2={modalData.subtitle2}
                saveText={tCommon("common:buttons.buttonYes")}
                closeText={tCommon("common:buttons.buttonNo")}
                error={error}
                endDate={endDate}
                onSave={() => exportIndicatorDataAsCsv()}
                isLoading={isLoading}
            />
            <Dropdown>
                <Dropdown.Toggle
                    id="dropdown-menu-icon-button"
                    variant="transparent"
                    className="border-0 ml-2"
                    size="lg"
                    bsPrefix="btn"
                >
                    <img src="../../print2.png" alt="Menu" style={{ width: '24px', height: '24px', margin: '8px' }} />
                </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {permissions.length > 0 && permissions.includes(PERMISSIONS.GLOBAL_DASHBOARD.PRINT || PERMISSIONS.CLAIMS.PRINT) && (
                                <Dropdown.Item onClick={openInfoModal}>
                                    {tCommon("common:dropdownButtonTitlePrint")}
                                </Dropdown.Item>
                            )}
                            {permissions.length > 0 && permissions.includes(PERMISSIONS.GLOBAL_DASHBOARD.SHARE || PERMISSIONS.CLAIMS.SHARE) && (
                                <Dropdown.Item onClick={openShareModal} >
                                    {tCommon("common:dropdownButtonTitleShare")}
                                </Dropdown.Item>
                            )}
                            {permissions.length > 0 && permissions.includes(PERMISSIONS.GLOBAL_DASHBOARD.EXPORT || PERMISSIONS.CLAIMS.EXPORT) && (
                                <Dropdown.Item onClick={openExportModal}>
                                    {tCommon("common:dropdownButtonTitleExport")}
                                </Dropdown.Item>
                            )}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
    };

    export default ChartShareActions;


